import { useState, useEffect } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Phone, CheckCircle2, AlertCircle, Loader2, X } from "lucide-react";
import { toast } from "sonner";

type PhoneStatus = "not_added" | "pending" | "verified";

export function PhoneNumberManagement() {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [step, setStep] = useState<"input" | "verify">("input");
  const [countdown, setCountdown] = useState(0);
  const [isResending, setIsResending] = useState(false);

  // Queries and mutations
  const { data: phoneStatus, isLoading: statusLoading, refetch: refetchStatus } = trpc.admin.phoneManagement.getPhoneStatus.useQuery();

  const addPhoneMutation = trpc.admin.phoneManagement.addPhoneNumber.useMutation({
    onSuccess: (data) => {
      toast.success(data.message);
      setStep("verify");
      setPhoneNumber("");
      setCountdown(data.expiryMinutes * 60);
      refetchStatus();
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const verifyMutation = trpc.admin.phoneManagement.verifyPhoneNumber.useMutation({
    onSuccess: (data) => {
      toast.success(data.message);
      setStep("input");
      setVerificationCode("");
      refetchStatus();
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const resendMutation = trpc.admin.phoneManagement.resendVerificationCode.useMutation({
    onSuccess: (data) => {
      toast.success(data.message);
      setCountdown(data.expiryMinutes * 60);
      setIsResending(false);
    },
    onError: (error) => {
      toast.error(error.message);
      setIsResending(false);
    },
  });

  const removeMutation = trpc.admin.phoneManagement.removePhoneNumber.useMutation({
    onSuccess: (data) => {
      toast.success(data.message);
      setStep("input");
      setPhoneNumber("");
      refetchStatus();
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  // Countdown timer
  useEffect(() => {
    if (countdown <= 0) return;

    const timer = setInterval(() => {
      setCountdown((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [countdown]);

  const formatCountdown = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  if (statusLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Phone className="w-5 h-5" />
            Phone Number Management
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Phone className="w-5 h-5" />
          Phone Number Management
        </CardTitle>
        <CardDescription>
          Add and verify your phone number to receive SMS notifications
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Current Status */}
        {phoneStatus && (
          <div className="space-y-3">
            <h3 className="font-semibold text-sm">Current Status</h3>
            {phoneStatus.isVerified ? (
              <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
                <div className="flex-1">
                  <p className="font-medium text-green-900">Verified</p>
                  <p className="text-sm text-green-700">{phoneStatus.phoneNumber}</p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    if (confirm("Are you sure you want to remove this phone number?")) {
                      removeMutation.mutate();
                    }
                  }}
                  disabled={removeMutation.isPending}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : phoneStatus.hasPhoneNumber ? (
              <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <AlertCircle className="w-5 h-5 text-amber-600" />
                <div className="flex-1">
                  <p className="font-medium text-amber-900">Pending Verification</p>
                  <p className="text-sm text-amber-700">{phoneStatus.phoneNumber}</p>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <AlertCircle className="w-5 h-5 text-gray-600" />
                <div>
                  <p className="font-medium text-gray-900">No Phone Number</p>
                  <p className="text-sm text-gray-700">Add a phone number to enable SMS notifications</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Add Phone Number Form */}
        {(!phoneStatus?.hasPhoneNumber || phoneStatus?.isVerified) && step === "input" && (
          <div className="space-y-3">
            <h3 className="font-semibold text-sm">
              {phoneStatus?.isVerified ? "Change Phone Number" : "Add Phone Number"}
            </h3>
            <div className="space-y-2">
              <Input
                type="tel"
                placeholder="+66812345678 or 0812345678"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                disabled={addPhoneMutation.isPending}
                className="font-mono"
              />
              <p className="text-xs text-muted-foreground">
                Supports Thai (+66, 0) and international formats
              </p>
            </div>
            <Button
              onClick={() => addPhoneMutation.mutate({ phoneNumber })}
              disabled={!phoneNumber || addPhoneMutation.isPending}
              className="w-full"
            >
              {addPhoneMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending Code...
                </>
              ) : (
                "Send Verification Code"
              )}
            </Button>
          </div>
        )}

        {/* Verification Form */}
        {step === "verify" && (
          <div className="space-y-3">
            <h3 className="font-semibold text-sm">Enter Verification Code</h3>
            <p className="text-sm text-muted-foreground">
              We've sent a 6-digit code to your phone. Enter it below.
            </p>
            <div className="space-y-2">
              <Input
                type="text"
                placeholder="000000"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                maxLength={6}
                disabled={verifyMutation.isPending}
                className="font-mono text-center text-lg tracking-widest"
              />
              <p className="text-xs text-muted-foreground">
                Code expires in {formatCountdown(countdown)}
              </p>
            </div>

            <div className="space-y-2">
              <Button
                onClick={() => verifyMutation.mutate({ code: verificationCode })}
                disabled={verificationCode.length !== 6 || verifyMutation.isPending}
                className="w-full"
              >
                {verifyMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  "Verify Code"
                )}
              </Button>

              <Button
                variant="outline"
                onClick={() => {
                  setIsResending(true);
                  resendMutation.mutate();
                }}
                disabled={countdown > 0 || resendMutation.isPending || isResending}
                className="w-full"
              >
                {isResending || resendMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Resending...
                  </>
                ) : countdown > 0 ? (
                  `Resend in ${formatCountdown(countdown)}`
                ) : (
                  "Resend Code"
                )}
              </Button>

              <Button
                variant="ghost"
                onClick={() => {
                  setStep("input");
                  setVerificationCode("");
                }}
                className="w-full"
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* SMS Notifications Info */}
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-900">
            <strong>Note:</strong> SMS notifications are currently in beta. Make sure your phone number is correct to receive important alerts about your account.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
